import React, { useEffect, useState } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { CircularProgress } from '@mui/material';
import PreSchoolService from 'Lib/API/PreSchool';
import { toastr } from 'react-redux-toastr';
import { getCurrency } from 'helper/numberFormat';
import { formatDate } from 'helper/dateFormat';
import { isEmpty } from 'lodash';
import ShareIcon from 'assets/icons/SharePaymentIcon.svg';
import { PulseLoader } from 'react-spinners';
import { SecondRow, Status } from './Theme';
import UserDetailsTabComponent from './UserDetailsTabComponent';
import './ParticipantDrawer.scss';

const style = {
  fontWeight: '700 !important',
  fontFamily: "'Poppins' !important",
  fontSize: 18,
  color: '#3f4255',
  marginBottom: 10,
};
const heading = {
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: 15,
  color: '#4f4f4f',
};
const content = {
  fontStyle: 'normal',
  fontWeight: 700,
  fontSize: 16,
  color: '#4f4f4f',
};
const box = {
  boxShadow: '0 0 50px 0 rgba(82, 63, 105, .15)',
  padding: '15px 20px',
  borderRadius: 11,
};
const SkeletonRow = () => (
  <Box display="flex" justifyContent="space-between" sx={{ mb: 2 }}>
    <Skeleton variant="text" width="40%" height={24} />
    <Skeleton variant="text" width="30%" height={24} />
  </Box>
);

const ParticipantDrawer = ({ drawerData, closeModal, handleClose, rows, onSharePaymentLinkClick, getClassParticipants, openModalCharge, closeModalCharge }) => {
  const [loading, setLoading] = useState(true);
  const [participantInfo, setParticipantInfo] = useState({});
  const { getAccessTokenSilently } = useAuth0();

  const getParticipantById = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const response = await PreSchoolService.getParticipantById(token, drawerData?._id);
      setParticipantInfo(response?.result);
    } catch (error) {
      toastr.error('Error', error?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getParticipantById();
  }, []);

  const afterUpdatedParticipantHandler = () => {
    handleClose();
    getClassParticipants();
  };

  return (
    <div className="participantDrawer__container">
      <Box
        className="header"
        sx={{ borderBottom: '1px solid #ccc', pb: 1, mb: '0px !important' }}
      >
        <Box className="title">
          <Typography className="name">
            {participantInfo?.firstName} {participantInfo?.lastName}
          </Typography>
          {participantInfo?.addedBy && (
            <div className="participant__name-tag">Added by {participantInfo?.addedBy}</div>
          )}
        </Box>
      </Box>

      {/* Status */}
      <Box sx={{ mt: 1, mb: 2 }}>
        <Typography style={style}>Status</Typography>
        <Box style={box}>
          {loading ? (
            <SkeletonRow />
          ) : (
            <Box display="flex" justifyContent="space-between" width="100%">
              <Typography style={heading}>Registration Status</Typography>
              <Typography style={content}>
                <Status type={participantInfo?.registrationStatus || 'pending'}>
                  {participantInfo?.registrationStatus || 'pending'}
                </Status>
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* User Details */}
      <Box sx={{ mt: 1, mb: 2 }}>
        <Typography style={style}>User Details</Typography>
        <Box style={box}>
          {loading ? (
            <>
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
            </>
          ) : participantInfo && (
            <>
              <Box display="grid" gridTemplateColumns="160px 1fr" rowGap="12px" columnGap="20px" marginBottom="8px">
                <Box display="flex" alignItems="center">
                  <Typography style={heading}>First Name</Typography>
                </Box>
                <Typography style={content}>{participantInfo?.firstName || '-'}</Typography>
              </Box>
              <Box display="grid" gridTemplateColumns="160px 1fr" rowGap="12px" columnGap="20px" marginBottom="8px">
                <Box display="flex" alignItems="center">
                  <Typography style={heading}>Last Name</Typography>
                </Box>
                <Typography style={content}>{participantInfo?.lastName || '-'}</Typography>
              </Box>
              <Box display="grid" gridTemplateColumns="160px 1fr" rowGap="12px" columnGap="20px" marginBottom="8px">
                <Box display="flex" alignItems="center">
                  <Typography style={heading}>Date of Birth</Typography>
                </Box>
                <Typography style={content}>{participantInfo?.dateOfBirth ? formatDate({ date: participantInfo.dateOfBirth, formatType: 'defaultWithUTC' }) : '-'}</Typography>
              </Box>
              <Box display="grid" gridTemplateColumns="160px 1fr" rowGap="12px" columnGap="20px" marginBottom="8px">
                <Box display="flex" alignItems="center">
                  <Typography style={heading}>School Grade</Typography>
                </Box>
                <Typography style={content}>{participantInfo?.class?.class_details?.grade || '-'}</Typography>
              </Box>
              <Box display="grid" gridTemplateColumns="160px 1fr" rowGap="12px" columnGap="20px" marginBottom="8px">
                <Box display="flex" alignItems="center">
                  <Typography style={heading}>Class</Typography>
                </Box>
                <Typography style={content}>{participantInfo?.class?.class_name || '-'}</Typography>
              </Box>
              <UserDetailsTabComponent
                participantInfo={participantInfo}
                afterUpdatedParticipantHandler={afterUpdatedParticipantHandler}
              />
            </>
          )}
        </Box>

        {/* Billing Summary */}
        {!isEmpty(participantInfo?.transactionInfo?.transactions) && (
          <Box sx={{ mt: 1, mb: 2 }}>
            <Typography style={style}>Billing Summary</Typography>
            <Box style={{ ...box, paddingLeft: '0px', paddingRight: '0px' }}>
              {participantInfo?.transactionInfo?.transactions?.map((el, i) => (
                <Box
                  key={i}
                  display="flex"
                  justifyContent="space-between"
                  style={{ paddingLeft: '20px', paddingRight: '20px' }}
                  sx={{ mb: 1 }}
                >
                  <Typography
                    style={{ ...heading, color: 'rgb(36 85 239)', textDecoration: 'underline', cursor: 'pointer' }}
                    onClick={() => openModalCharge(el?.transactionId)}
                  >
                    {el?.Ledger?.name}
                  </Typography>
                  <Typography style={content}>
                    {getCurrency()}{Number(el?.amount ?? 0).toFixed(2)}
                  </Typography>
                </Box>
              ))}
              <hr style={{ marginLeft: '20px', marginRight: '20px' }} />
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }} style={{ paddingLeft: '20px', paddingRight: '20px' }}>
                <Typography style={{ ...heading, marginRight: 10 }}>Total Billed</Typography>
                <Typography style={content}>
                  {getCurrency()}{Number(participantInfo?.transactionInfo?.totalAmountTransaction ?? 0).toFixed(2)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }} style={{ paddingLeft: '20px', paddingRight: '20px' }}>
                <Typography style={{ ...heading, marginRight: 10 }}>Total Paid</Typography>
                <Typography style={{ ...content, color: '#16A34A' }}>
                  {getCurrency()}{Number(participantInfo?.transactionInfo?.totalPaid ?? 0).toFixed(2)}
                </Typography>
              </Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                style={{ ...box, backgroundColor: '#EAF1FF', paddingLeft: '10px', paddingRight: '10px', marginLeft: '10px', marginRight: '10px', borderRadius: '10px' }}
                sx={{ mb: 1 }}
              >
                <Typography style={{ ...content, marginRight: 10, color: '#3561DC' }}>Balance Due</Typography>
                <Typography style={{ ...content, color: '#3561DC' }}>
                  {getCurrency()}{Number((participantInfo?.transactionInfo?.totalAmountTransaction - participantInfo?.transactionInfo?.totalPaid) ?? 0).toFixed(2)}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* Share Payment Link */}
        {participantInfo?.transactionInfo?.totalAmountTransaction - participantInfo?.transactionInfo?.totalPaid > 0 && (
          <SecondRow onClick={() => onSharePaymentLinkClick(drawerData)} disabled={loading}>
            {loading ? (
              <PulseLoader color={'#fff'} size={'7px'} />
            ) : (
              <><img src={ShareIcon} alt="Share-Payment" /> Share Payment Link</>
            )}
          </SecondRow>
        )}

        {/* Products */}
        {!isEmpty(participantInfo?.products) && (
          <Box sx={{ mt: 1, mb: 2 }}>
            <Typography style={style}>Products</Typography>
            <Box style={box}>
              {participantInfo?.products?.map((el, i) => {
                if (el?.variant && typeof el.variant === 'object') {
                  return Object.entries(el.variant).map(([variantName, qty]) => (
                    <Box key={`${i}-${variantName}`} display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography style={heading}>{el?.title} ({variantName})</Typography>
                      <Typography style={content}>{qty}</Typography>
                    </Box>
                  ));
                }
                return (
                  <Box key={i} display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography style={heading}>{el?.title}</Typography>
                    <Typography style={content}>{el?.quantity ?? el?.amount ?? 0}</Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </div>
  );
};

export default ParticipantDrawer;
